/**
 * Step 15 (folder 18) — Handoff to /chat — SYNTHESIS variant
 *
 * Spec: specs/onboarding-rebuild.md → Act 3 step 15
 *
 * Current shape (2026-05-17 minimalist redesign — Mason locks):
 *
 *   ┌─────────────────────────────────────────────────────────┐
 *   │                                                          │
 *   │              [ Trial active · 7 days left ]              │ ← plan-tier pill
 *   │                   renews May 24, 2026                    │   (varies by tier)
 *   │                                                          │
 *   │                  Ready, Mason?                           │ ← single headline
 *   │                                                          │   (name in gold)
 *   │              [  Take me in  →  ]                         │ ← CTA
 *   │              (20 messages/month on Free)                 │   (free tier only)
 *   │                                                          │
 *   └─────────────────────────────────────────────────────────┘
 *           ↓ tap CTA → doors part, gold light spills in,
 *             navigate to /chat (~700ms)
 *
 * Mason cut 2026-05-17 (over-ceremony — was eating activation):
 *   - "The beginning" eyebrow
 *   - Callback chip ("Your first question is saved" / "Grace is holding...")
 *   - "Grace is waiting on the other side" benediction
 *   - Expanding gold rule
 *   - Door-reveal mock chat preview (fake header + Grace bubble + input)
 *   - "Take me to Grace" CTA → "Take me in" (no persona-pinning)
 *
 * Preserved (Mason locks):
 *   - Door-open animation on tap (the distinctive visual)
 *   - 700ms door duration
 *   - CTA hover-lift handlers (1px transform, gold glow ring)
 *   - Plan-tier pill (trial / annual / founding / free)
 *   - Reduced-motion path (instant elapsed → sequenceEnd)
 *   - Tap-to-skip during intro
 *
 * Time-to-interactive: ~1.4s (was ~3.6s pre-redesign — 60% faster).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// Trial length in days — drives the renewal-date hint under the trial pill.
// Matches PaywallTrial.synthesis.jsx's TRIAL_DAYS constant (Stripe Payment
// Link is configured with trial_period_days: 7).
const TRIAL_DAYS = 7

// Timing — minimalist redesign 2026-05-17. Mason cut the eyebrow / chip /
// benediction / gold-rule reveals. What remains: pre-roll → "Ready, {name}?"
// (with plan pill on the same reveal) → CTA. Door open animation preserved
// on tap (Mason explicitly liked the doors). ~1.4s to interactive vs old
// ~3.6s — 60% faster while keeping the distinctive door-reveal mechanic.
const PRE_ROLL_MS = 220
const READY_REVEAL_MS = 520
const CTA_DELAY_MS = 180
const CTA_REVEAL_MS = 460
const OPEN_DURATION_MS = 700

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'

// Door-reveal mock chat retired 2026-05-17 (Mason lock). Previous version
// showed a fake "Chat with Grace" header + Grace bubble ("Ready when you
// are.") + Message input. Mason cut all of it — the doors now part to
// reveal a subtle gold glow instead (light "spilling in"). Cleaner, faster,
// no fake UI or text the user has to read before navigating.

function clamp(n, lo, hi) {
  if (Number.isNaN(n)) return lo
  if (n < lo) return lo
  if (n > hi) return hi
  return n
}

function clamp01(n) {
  return clamp(n, 0, 1)
}

// Module-scope DoorPanel per static-components lint rule.
function DoorPanel({ side, opening, reducedMotion }) {
  const isLeft = side === 'left'
  const transformClosed = 'translateX(0%)'
  const transformOpen = isLeft ? 'translateX(-101%)' : 'translateX(101%)'
  return (
    <div
      aria-hidden="true"
      className="absolute top-0 bottom-0 pointer-events-none"
      style={{
        width: '50.5%',
        left: isLeft ? 0 : 'auto',
        right: isLeft ? 'auto' : 0,
        background: 'linear-gradient(180deg, rgba(7, 12, 28, 0.98) 0%, rgba(4, 8, 20, 0.98) 100%)',
        transform: opening ? transformOpen : transformClosed,
        transition: reducedMotion
          ? 'none'
          : `transform ${OPEN_DURATION_MS}ms cubic-bezier(0.65, 0, 0.35, 1)`,
        boxShadow: isLeft
          ? 'inset -1px 0 0 rgba(233, 195, 73, 0.35), -8px 0 24px -8px rgba(233, 195, 73, 0.15) inset'
          : 'inset 1px 0 0 rgba(233, 195, 73, 0.35), 8px 0 24px -8px rgba(233, 195, 73, 0.15) inset',
      }}
    />
  )
}

// Module-scope reduced-motion hook.
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = e => setReduced(e.matches)
    if (mq.addEventListener) mq.addEventListener('change', onChange)
    else mq.addListener(onChange)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange)
      else mq.removeListener(onChange)
    }
  }, [])
  return reduced
}

// Compute the trial renewal date — today + TRIAL_DAYS. Memoized so it's
// stable across re-renders (matches PaywallTrial.synthesis's pattern).
// Called unconditionally per React rules even though only the `trial`
// variant displays the result.
function useTrialRenewalDate() {
  return useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() + TRIAL_DAYS)
    return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
  }, [])
}

// Module-scope plan-tier pill. Uniform-treatment 2026-05-17 (Mason lock):
// all paid tiers share the same font/size/color (subtle gold-select bg +
// gold text + gold-dim border). Variants:
//   - trial     → "Trial active · 7 days left" + muted "renews <date>" hint
//   - annual    → "Premium Annual"
//   - founding  → "Founding Member" + gold star glyph (only structural diff)
//   - free      → no pill; "20 messages/month" hint near the CTA instead.
function PlanTierPill({ tier, revealStyle, renewalDate }) {
  if (tier === 'free' || !tier) return null

  if (tier === 'trial') {
    return (
      <div className="flex flex-col items-center gap-1.5" style={revealStyle}>
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-sans font-semibold uppercase tracking-[0.18em]"
          style={{
            background: 'var(--gold-select)',
            border: '1px solid var(--gold-dim)',
            color: 'var(--gold)',
            lineHeight: 1,
          }}
        >
          <span>Trial active · {TRIAL_DAYS} days left</span>
        </span>
        <span className="text-[11px] italic text-[var(--text-muted)] font-body">
          renews {renewalDate}
        </span>
      </div>
    )
  }

  if (tier === 'annual') {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-sans font-semibold uppercase tracking-[0.18em]"
        style={{
          ...revealStyle,
          background: 'var(--gold-select)',
          border: '1px solid var(--gold-dim)',
          color: 'var(--gold)',
          lineHeight: 1,
        }}
      >
        <span>Premium Annual</span>
      </span>
    )
  }

  if (tier === 'founding') {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-sans font-semibold uppercase tracking-[0.18em]"
        style={{
          ...revealStyle,
          background: 'var(--gold-select)',
          border: '1px solid var(--gold-dim)',
          color: 'var(--gold)',
          lineHeight: 1,
        }}
      >
        {/* Star glyph — inline SVG, inherits gold via currentColor. Only
            structural difference from annual (Mason 2026-05-17). */}
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
          style={{ flexShrink: 0, display: 'block' }}
        >
          <path d="M12 2l2.95 6.91 7.42.64-5.63 4.88 1.72 7.27L12 17.77l-6.46 3.93 1.72-7.27L1.63 9.55l7.42-.64L12 2z" />
        </svg>
        <span style={{ lineHeight: 1 }}>Founding Member</span>
      </span>
    )
  }

  return null
}

export default function HandoffSynthesis({
  displayName,
  firstName,
  onContinue,
  // Prop chain (OnboardingPreview → index.jsx → here) still wires
  // firstConversationId through even though the 2026-05-17 redesign
  // dropped the callback chip that consumed it. Kept available so a
  // future "we saved your demo conversation" hint can re-use it without
  // re-plumbing.
  // eslint-disable-next-line no-unused-vars
  firstConversationId,
  planTier,
}) {
  const reducedMotion = usePrefersReducedMotion()
  const name = (firstName && firstName.trim()) || (displayName && displayName.trim()) || 'friend'
  // Trial renewal date — only used when planTier === 'trial', but the hook
  // must be called unconditionally per React rules.
  const trialRenewalDate = useTrialRenewalDate()
  const isFree = planTier === 'free' || !planTier

  const [elapsed, setElapsed] = useState(0)
  const [opening, setOpening] = useState(false)
  const cardRef = useRef(null)

  // Schedule offsets — minimalist 2026-05-17. Pill + headline share the
  // ready reveal so they land together; CTA follows after a brief gap.
  const readyStart = PRE_ROLL_MS
  const readyEnd = readyStart + READY_REVEAL_MS
  const ctaStart = readyEnd + CTA_DELAY_MS
  const ctaEnd = ctaStart + CTA_REVEAL_MS
  const sequenceEnd = ctaEnd + 200

  useEffect(() => {
    if (reducedMotion) {
      setElapsed(sequenceEnd)
      return undefined
    }
    let raf = 0
    let started = null
    const tick = now => {
      if (started === null) started = now
      const next = now - started
      setElapsed(next)
      if (next < sequenceEnd) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reducedMotion])

  const skipIntroToEnd = useCallback(() => {
    if (!opening) setElapsed(sequenceEnd)
  }, [opening, sequenceEnd])

  // Reveal progress (0..1).
  const readyP = clamp01((elapsed - readyStart) / READY_REVEAL_MS)
  const ctaP = clamp01((elapsed - ctaStart) / CTA_REVEAL_MS)
  const ctaReady = ctaP > 0.5

  const handleContinue = useCallback(() => {
    if (!ctaReady || opening) return
    if (reducedMotion) {
      onContinue?.()
      return
    }
    setOpening(true)
    window.setTimeout(() => {
      onContinue?.()
    }, OPEN_DURATION_MS + 60)
  }, [ctaReady, opening, reducedMotion, onContinue])

  // Pill + headline reveal style — shared so they fade in together.
  const readyRevealStyle = {
    opacity: readyP,
    transform: `translateY(${(1 - readyP) * 10}px)`,
    transition: 'opacity 520ms ease-out, transform 520ms ease-out',
  }

  return (
    <div
      ref={cardRef}
      onClick={elapsed < sequenceEnd && !opening ? skipIntroToEnd : undefined}
      className="relative w-full max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto rounded-2xl overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse at top, rgba(13, 22, 47, 1) 0%, rgba(7, 12, 28, 1) 60%, rgba(4, 8, 20, 1) 100%)',
        minHeight: 'clamp(520px, 78vh, 760px)',
      }}
      aria-label="Onboarding complete — handoff to chat"
    >
      {/* Subtle paper grain */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage: 'radial-gradient(rgba(233,195,73,0.5) 1px, transparent 1px)',
          backgroundSize: '3px 3px',
        }}
      />

      {/* Behind-the-doors content — gold glow "spilling in" when the doors
          part. Replaced the fake chat preview (header + Grace bubble + Message
          input) with a warm radial gold gradient suggesting light beyond the
          threshold (Mason 2026-05-17 lock — no text the user has to read
          before navigating). */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: opening ? 1 : 0,
          transition: 'opacity 360ms ease-out',
          background:
            'radial-gradient(ellipse at center, rgba(233, 195, 73, 0.35) 0%, rgba(233, 195, 73, 0.18) 30%, rgba(13, 22, 47, 0.7) 70%, rgba(7, 12, 28, 0.95) 100%)',
        }}
      />

      {/* Door panels */}
      <DoorPanel side="left" opening={opening} reducedMotion={reducedMotion} />
      <DoorPanel side="right" opening={opening} reducedMotion={reducedMotion} />

      {/* Foreground content */}
      <div
        className="relative z-10 flex flex-col items-center text-center px-5 sm:px-10 py-10 sm:py-14 gap-5 sm:gap-6"
        style={{
          opacity: opening ? 0 : 1,
          transition: 'opacity 280ms ease-out',
          pointerEvents: opening ? 'none' : 'auto',
        }}
      >
        {/* Plan-tier pill — fades in alongside the headline. Hidden for
            free tier (the "20 messages/month" hint renders under the CTA
            instead). */}
        <PlanTierPill
          tier={planTier}
          revealStyle={readyRevealStyle}
          renewalDate={trialRenewalDate}
        />

        {/* "Ready, {name}?" — single headline. Name in gold. */}
        <h1
          className="font-heading text-[var(--text-primary)] text-3xl sm:text-4xl font-medium leading-tight italic"
          style={readyRevealStyle}
        >
          Ready, <span style={{ color: 'var(--gold)' }}>{name}</span>?
        </h1>

        {/* CTA — professional hover handlers (1px lift + gold glow ring). */}
        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            handleContinue()
          }}
          disabled={!ctaReady || opening}
          aria-label={`Take ${name} in`}
          className="group relative inline-flex items-center justify-center gap-2.5 bg-[var(--gold)] text-[var(--text-on-gold)] font-semibold text-[15px] tracking-[0.005em] px-9 py-3.5 rounded-full select-none focus:outline-none disabled:cursor-default"
          style={{
            opacity: ctaP,
            transform: `translateY(${(1 - ctaP) * 10}px)`,
            transitionProperty: 'opacity, transform, box-shadow, filter',
            transitionDuration: '460ms, 460ms, 320ms, 240ms',
            transitionTimingFunction: `${EASE}, ${EASE}, ${EASE}, ${EASE}`,
            boxShadow:
              '0 1px 0 rgba(255,255,255,0.18) inset, 0 6px 18px -6px rgba(233, 195, 73, 0.45)',
          }}
          onMouseEnter={e => {
            if (!ctaReady) return
            e.currentTarget.style.transform = `translateY(${(1 - ctaP) * 10 - 1}px)`
            e.currentTarget.style.boxShadow =
              '0 1px 0 rgba(255,255,255,0.22) inset, 0 12px 28px -8px rgba(233, 195, 73, 0.55), 0 0 0 1px rgba(233, 195, 73, 0.45)'
            e.currentTarget.style.filter = 'brightness(1.04)'
          }}
          onMouseLeave={e => {
            if (!ctaReady) return
            e.currentTarget.style.transform = `translateY(${(1 - ctaP) * 10}px)`
            e.currentTarget.style.boxShadow =
              '0 1px 0 rgba(255,255,255,0.18) inset, 0 6px 18px -6px rgba(233, 195, 73, 0.45)'
            e.currentTarget.style.filter = 'brightness(1)'
          }}
        >
          <span>Take me in</span>
          <span
            aria-hidden="true"
            className="transition-transform duration-300 group-hover:translate-x-1"
          >
            &rarr;
          </span>
        </button>

        {/* Free-tier hint — only shows when planTier is 'free' (or absent).
            Gated on ctaP so it doesn't pop in before the button is interactive.
            Lives inside the foreground container so it fades with the door
            opacity transition. */}
        {isFree && (
          <p
            className="text-[11px] text-[var(--text-muted)] italic font-body"
            style={{
              opacity: ctaP,
              transition: 'opacity 460ms ease-out',
            }}
          >
            20 messages/month on Free · upgrade anytime
          </p>
        )}
      </div>

      {/* Skip hint during intro */}
      {elapsed < sequenceEnd && !reducedMotion && !opening && (
        <p
          aria-hidden="true"
          className="hidden sm:block absolute bottom-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] text-[var(--text-muted)] opacity-60 pointer-events-none"
        >
          tap to skip
        </p>
      )}
    </div>
  )
}
